import React, { useState, useEffect } from 'react';

/**
 * Live Update Test Component
 * Tests and monitors real-time WebSocket vehicle updates
 */
const LiveUpdateTest = ({ publicVehicles, userVehicles, sharedVehicles }) => {
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [vehicleUpdates, setVehicleUpdates] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(true);

  // Track vehicle location changes
  useEffect(() => {
    if (!isMonitoring) return;

    const allVehicles = [...(publicVehicles || []), ...(userVehicles || []), ...(sharedVehicles || [])];
    
    // Check for new updates
    allVehicles.forEach(vehicle => {
      if (vehicle.last_updated) {
        const updateTime = new Date(vehicle.last_updated);
        const now = new Date();
        const timeDiff = now - updateTime;
        
        // If update is within last 30 seconds, consider it a live update
        if (timeDiff < 30000) {
          const updateInfo = {
            vehicleId: vehicle.id,
            vehicleType: vehicle.vehicle_type,
            location: vehicle.last_location,
            timestamp: vehicle.last_updated,
            timeDiff: Math.round(timeDiff / 1000) + 's ago'
          };
          
          setVehicleUpdates(prev => {
            const filtered = prev.filter(u => !(u.vehicleId === vehicle.id && u.timestamp === vehicle.last_updated));
            return [updateInfo, ...filtered].slice(0, 20); // Keep last 20 updates
          });
          
          setLastUpdate(updateInfo);
          setUpdateCount(prev => prev + 1);
        }
      }
    });
  }, [publicVehicles, userVehicles, sharedVehicles, isMonitoring]);

  // Generate test WebSocket message (for testing)
  const sendTestLocationUpdate = () => {
    // Create a synthetic location update for testing
    const testUpdate = {
      type: 'location_update',
      vehicle_id: 'test_vehicle_123',
      location: {
        latitude: 17.2515 + (Math.random() - 0.5) * 0.01,
        longitude: -88.7611 + (Math.random() - 0.5) * 0.01
      },
      timestamp: new Date().toISOString()
    };
    
    // Dispatch a custom event that the hook can listen to for testing
    window.dispatchEvent(new CustomEvent('test-websocket-message', {
      detail: testUpdate
    }));
  };

  const clearUpdates = () => {
    setVehicleUpdates([]);
    setUpdateCount(0);
    setLastUpdate(null);
  };

  const totalVehicles = (publicVehicles?.length || 0) + (userVehicles?.length || 0) + (sharedVehicles?.length || 0);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">🚗 Live Update Monitor</h3>
        <div className="space-x-2">
          <button 
            onClick={() => setIsMonitoring(!isMonitoring)}
            className={`px-3 py-2 rounded ${isMonitoring ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
          >
            {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
          </button>
          <button 
            onClick={sendTestLocationUpdate}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Send Test Update
          </button>
          <button 
            onClick={clearUpdates}
            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">📊 Vehicle Count</h4>
          <div className="text-2xl font-bold text-blue-600">{totalVehicles}</div>
          <div className="text-sm text-blue-600">
            {publicVehicles?.length || 0} public, {userVehicles?.length || 0} user, {sharedVehicles?.length || 0} shared
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">📡 Live Updates</h4>
          <div className="text-2xl font-bold text-green-600">{updateCount}</div>
          <div className="text-sm text-green-600">
            {isMonitoring ? 'Monitoring active' : 'Monitoring paused'}
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-semibold text-purple-800 mb-2">⏰ Last Update</h4>
          <div className="text-sm text-purple-600">
            {lastUpdate ? (
              <>
                <div className="font-mono">{lastUpdate.vehicleId}</div>
                <div>{lastUpdate.timeDiff}</div>
              </>
            ) : (
              'No recent updates'
            )}
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">🔍 Status</h4>
          <div className={`text-sm font-semibold ${
            updateCount > 0 ? 'text-green-600' : 
            totalVehicles > 0 ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {updateCount > 0 ? '✅ Live Updates Working' : 
             totalVehicles > 0 ? '⚠️ No Live Updates' : '❌ No Vehicles'}
          </div>
        </div>
      </div>

      {/* Recent Updates List */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">📋 Recent Updates (Last 30 seconds)</h4>
        {vehicleUpdates.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {vehicleUpdates.map((update, index) => (
              <div key={`${update.vehicleId}-${update.timestamp}-${index}`} 
                   className="bg-white rounded p-3 border-l-4 border-green-400">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-sm font-semibold text-gray-800">
                      {update.vehicleId}
                    </div>
                    <div className="text-xs text-gray-600">
                      {update.vehicleType} • {update.timeDiff}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600">
                      {update.location?.latitude?.toFixed(4)}, {update.location?.longitude?.toFixed(4)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(update.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {isMonitoring ? (
              <>
                <div className="text-4xl mb-2">⏳</div>
                <div>Waiting for live updates...</div>
                <div className="text-sm mt-1">Vehicle locations should update automatically when they move</div>
              </>
            ) : (
              <>
                <div className="text-4xl mb-2">⏸️</div>
                <div>Monitoring paused</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Debug Information */}
      <div className="mt-6 bg-gray-100 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-2">🔧 Debug Information</h4>
        <div className="text-xs text-gray-600 space-y-1">
          <div>• Live updates should appear automatically when vehicles move</div>
          <div>• If no updates appear after 1-2 minutes, check WebSocket connection in browser console</div>
          <div>• Look for "📨 WebSocket message received:" logs in console</div>
          <div>• "Send Test Update" creates a synthetic update for testing</div>
          <div>• Updates are considered "live" if they occurred within the last 30 seconds</div>
        </div>
      </div>
    </div>
  );
};

export default LiveUpdateTest;